import { UserIcon } from "@xola/icons";
import clsx from "clsx";
import { Dayjs } from "dayjs";
import React from "react";
import { useHistory } from "react-router-dom";
import { NoTranslate } from "@/components/Common/NoTranslate";
import { TimelineItem, TimelineItemReason } from "@/components/PurchasesMenu/Timeline/TimelineItem";

// Merge activity is inferred from `mergedInto` on the absorbed record or `mergedTravelers` on the survivor.
// The latter can be a map-level diff or a diff keyed by traveler ID.
type ReferenceDiff = { old?: { id?: ID } | null; new?: { id?: ID } | null };
type MergedTravelersFlatDiff = { old?: Record<string, { id: ID }> | null; new?: Record<string, { id: ID }> | null };
type MergedTravelersPerIdDiff = Record<ID, { old?: { id: ID } | null; new?: { id: ID } | null }>;
type MergedTravelersDiff = MergedTravelersFlatDiff | MergedTravelersPerIdDiff;

const isFlatMergedTravelersDiff = (diff: MergedTravelersDiff): diff is MergedTravelersFlatDiff =>
    "old" in diff || "new" in diff;

export interface TravelerMergeAudit {
    id: ID;
    createdAt: Dayjs;
    createdBy?: { id: ID };
    data?: { mergedInto?: ReferenceDiff; mergedTravelers?: MergedTravelersDiff };
    travelerNames?: Record<string, string>;
}

export interface MergeEvent {
    isMerged: boolean;
    isSurvivorSide: boolean;
    counterpartyId?: ID;
}

const getChangedId = (
    oldEntries?: Record<string, { id: ID }> | null,
    newEntries?: Record<string, { id: ID }> | null,
) => {
    const oldIds = Object.keys(oldEntries ?? {});
    const newIds = Object.keys(newEntries ?? {});
    const addedId = newIds.find((id) => !oldIds.includes(id));
    const removedId = oldIds.find((id) => !newIds.includes(id));

    return addedId ? { id: addedId, isMerged: true } : removedId ? { id: removedId, isMerged: false } : null;
};

export const getMergeEvent = (change: TravelerMergeAudit): MergeEvent | null => {
    const mergedInto = change.data?.mergedInto;
    if (mergedInto && (mergedInto.old?.id || mergedInto.new?.id)) {
        const isMerged = Boolean(mergedInto.new?.id);
        return { isMerged, isSurvivorSide: false, counterpartyId: isMerged ? mergedInto.new?.id : mergedInto.old?.id };
    }

    const mergedTravelers = change.data?.mergedTravelers;
    if (mergedTravelers) {
        if (isFlatMergedTravelersDiff(mergedTravelers)) {
            const change = getChangedId(mergedTravelers.old, mergedTravelers.new);
            if (change) {
                return { isMerged: change.isMerged, isSurvivorSide: true, counterpartyId: change.id };
            }
        } else {
            const [counterpartyId, entry] =
                Object.entries(mergedTravelers).find(([, diff]) => diff.old?.id ?? diff.new?.id) ?? [];
            if (counterpartyId && entry) {
                return { isMerged: Boolean(entry.new?.id), isSurvivorSide: true, counterpartyId };
            }
        }
    }

    return null;
};

interface MergeTimelineItemProps {
    change: TravelerMergeAudit;
    showDate?: boolean;
}

export const MergeTimelineItem = ({ change, showDate = false }: MergeTimelineItemProps) => {
    const history = useHistory();
    const audit = { id: change.id, createdAt: change.createdAt, createdBy: change.createdBy ?? { id: "" } };

    const event = getMergeEvent(change);
    const counterpartyId = event?.counterpartyId;
    const name = counterpartyId ? change.travelerNames?.[String(counterpartyId)] : undefined;
    const isNavigable = counterpartyId && !(event?.isMerged && event?.isSurvivorSide);

    const title = event?.isMerged ? "Customer Record Merged" : "Customer Record Unmerged";
    let message: React.ReactNode;
    if (event?.isMerged) {
        message = name ? (
            <>
                This customer record has been merged with <NoTranslate>{name}</NoTranslate>&apos;s record.
            </>
        ) : (
            "This customer record has been merged with another record."
        );
    } else if (event?.isSurvivorSide) {
        message = name ? (
            <>
                <NoTranslate>{name}</NoTranslate>&apos;s customer record has been unmerged from this record to a new
                record
            </>
        ) : (
            "A customer record has been unmerged from this record to a new record."
        );
    } else {
        message = name ? (
            <>
                This customer record has been unmerged from <NoTranslate>{name}</NoTranslate>&apos;s record.
            </>
        ) : (
            "This customer record was unmerged from the record it had been merged into."
        );
    }

    const handleClick = () => {
        if (isNavigable) {
            history.push(`/customers/contacts/${counterpartyId}`);
        }
    };

    return (
        <TimelineItem title={title} audit={audit} icon={<UserIcon />} color="primary" showDate={showDate}>
            <div className={clsx(isNavigable && "cursor-pointer")} onClick={isNavigable ? handleClick : undefined}>
                <TimelineItemReason>
                    <div>{message}</div>
                </TimelineItemReason>
            </div>
        </TimelineItem>
    );
};

MergeTimelineItem.supportsAudit = (change: TravelerMergeAudit) => getMergeEvent(change) !== null;
